import { SerializedError } from '@reduxjs/toolkit';
import { TriniumApiError } from './Trinium/types';

/**
 * Object used for parsing API Errors responses to Formik Fields.
 * If mapped to a field, error message will contain the string of the error.
 * If mapped to a sub-form, an array of strings will be provided for all of the errors on the sub-form.
 * 		Sub-form errors will be printed as "{subformFieldName}:{fieldErrorMessage};"
 */
export interface FormFieldError {
	fieldName?: string;
	errorMessage: string;
}

/**
 *
 * @param error
 * @returns
 */
function tryParseTriniumError(error: unknown): TriniumApiError | undefined {
	if (error && typeof error === 'object') {
		if ('data' in error) {
			// We are dealing with a FetchBaseQueryError that we can deserialize
			return JSON.parse(JSON.stringify(error.data)) as TriniumApiError;
		}
	}

	return undefined;
}

function findMappedProperty(
	map: Record<string, string | Record<string, string>>,
	property: string,
	ignoreSubformNesting?: boolean
): string | undefined {
	const mappedEntry = Object.entries(map).reduce((currentValue, [uiFieldName, apiFieldNameOrMap]) => {
		if (typeof apiFieldNameOrMap === 'object') {
			const foundSubformProperty = findMappedProperty(apiFieldNameOrMap, property, ignoreSubformNesting);

			if (foundSubformProperty) return `${uiFieldName}${ignoreSubformNesting ? '.' : ':'}${foundSubformProperty}`;
		} else if (apiFieldNameOrMap === property) {
			return uiFieldName;
		}

		return currentValue;
	}, '');

	/** Return the property name if it was found, otherwise explicitly return undefined */
	return mappedEntry ? mappedEntry : undefined;
}

/**
 * Generic method for retreiving form errors.
 * @param map Mapping Type correlating the UI Form type to the API Response type
 * @param error Raw RTK API error object
 * @param ignoreSubformNesting Originally designed to add a : between subformName:fieldName for formik, newer designed forms are not nested
 * and the field name is subformName.fieldName so the colon needs to be replaced with the . to access the subform field
 * @returns A collection of FormFieldErrors. If a field could not be mapped than fieldName will be undefined.
 */
export function getApiFormFieldErrors(
	map: Record<string, string | Record<string, string>>,
	error: unknown,
	ignoreSubformNesting?: boolean
) {
	/** Return collection */
	const fieldErrors: FormFieldError[] = [];

	/** Parse the raw API error response */
	const apiError = tryParseTriniumError(error);

	/** If it was able to be deserialized, process it */
	if (apiError && apiError.errors) {
		/** Loop over the error field names and determine if we can match them */
		Object.keys(apiError.errors).forEach((key) => {
			/** Ensuring we have what we need to continue */
			if (!apiError.errors || !apiError.errors[key]?.length) {
				return;
			}

			const mappedFieldName = findMappedProperty(map, key, ignoreSubformNesting);

			if (mappedFieldName) {
				/** Return the field name and the error message  */
				fieldErrors.push({
					fieldName: mappedFieldName,
					errorMessage: apiError.errors[key][0]
				});
			} else {
				/** Could not match to a field, set generic error */
				fieldErrors.push({
					fieldName: undefined,
					errorMessage: apiError.errors[key][0]
				});
			}
		});
	} else {
		fieldErrors.push({
			fieldName: undefined,
			errorMessage: getApiErrorMessage(error)
		});
	}

	return fieldErrors;
}

/**
 * Returns the extracted string of an RTK Endpoint Error object
 */
export function getApiErrorMessage(error: unknown) {
	const unknownError = 'Unknown Error';

	if (!error) {
		return unknownError;
	}

	/** Try to deserialize the error as an expected formatted error */
	const deserializedError: TriniumApiError | undefined = tryParseTriniumError(error);

	if (deserializedError) {
		if (deserializedError.detail) {
			return deserializedError.detail;
		} else if (deserializedError.errors) {
			return Object.keys(deserializedError.errors)
				.map((k) => {
					if (!deserializedError.errors) {
						return unknownError;
					} else {
						return deserializedError.errors[k]?.[0] ?? unknownError;
					}
				})
				.join(',');
		} else {
			return unknownError;
		}
	}

	if (typeof error === 'object') {
		if ('message' in error) {
			// We are dealing with a SerializedError
			return (error as SerializedError).message ?? unknownError;
		} else if ('detail' in error) {
			return String(error.detail);
		}
	}

	// Otherwise we don't know what was passed in
	return unknownError;
}
